// frontend/src/components/GlobalResultsDrawer.js
// Canonical UPS global search drawer.
// Reads assignment-backed and ban-backed sources directly so global search does
// not depend on legacy driver.status for grouping or routing.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { selectFilters } from '../features/ui/filtersSlice';
import {
  selectConcreteSelectedSiteId,
  selectIsAllSitesMode,
} from '../features/sites/sitesSlice';
import {
  fetchCompanyAssignments,
  fetchSiteAssignments,
} from '../services/assignmentService';
import { fetchBannedDrivers } from '../services/upsDriverService';

/* -------------------------------------------------------------------------- */
/* Constants                                                                  */
/* -------------------------------------------------------------------------- */

const ASSIGNMENT_STATUSES = ['active', 'confirmed', 'requested'];

const EMPTY_RESULTS = {
  Active: [],
  Reserved: [],
  Requests: [],
  Banned: [],
};

const ROUTE_KEY_BY_ASSIGNMENT_STATUS = {
  active: 'active',
  confirmed: 'reserved',
  requested: 'requests',
};

const TITLE_BY_ROUTE_KEY = {
  active: 'Active',
  reserved: 'Reserved',
  requests: 'Requests',
  banned: 'Banned',
};

/* -------------------------------------------------------------------------- */
/* Small value helpers                                                        */
/* -------------------------------------------------------------------------- */

const toNum = (value) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : undefined;
};

const firstPresent = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null) return value;
  }
  return undefined;
};

/* -------------------------------------------------------------------------- */
/* Search helpers                                                             */
/* -------------------------------------------------------------------------- */

// Used as a final client-side safety filter after backend search.
// Searchable fields must match what the UI displays.
const matchesSearch = (row, query) => {
  const haystack = `${row.name || ''} ${row.email || ''} ${
    row.vehicle_registration_number || row.vrn || ''
  }`.toLowerCase();

  return haystack.includes(query);
};

// Escapes user search text before using it in a RegExp.
const escapeRegExp = (value = '') =>
  String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Highlights the visible part of a field that matched the search.
// Example: query "yas" inside "yasin@gmail.com" highlights "yas".
const highlightMatch = (text, query) => {
  if (!text) return null;

  const safeText = String(text);
  const safeQuery = String(query || '').trim();

  if (!safeQuery) return safeText;

  const regex = new RegExp(`(${escapeRegExp(safeQuery)})`, 'ig');
  const parts = safeText.split(regex);

  return parts.map((part, index) => {
    const isMatch = part.toLowerCase() === safeQuery.toLowerCase();

    return isMatch ? (
      <span key={`${part}-${index}`} className="font-bold text-app-primary">
        {part}
      </span>
    ) : (
      <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
    );
  });
};

// Renders the searchable secondary driver fields.
// This makes it clear when a row matched by vehicle reg or email.
const renderDriverMeta = (driver, query) => {
  const fields = [driver.vehicle_registration_number, driver.email].filter(Boolean);

  if (!fields.length) return 'No vehicle reg or email';

  return fields.map((field, index) => (
    <React.Fragment key={`${field}-${index}`}>
      {index > 0 && ' • '}
      {highlightMatch(field, query)}
    </React.Fragment>
  ));
};

/* -------------------------------------------------------------------------- */
/* Normalizers                                                                */
/* -------------------------------------------------------------------------- */

// Converts assignment-backed rows into the shared drawer result shape.
const normalizeAssignmentResult = (row) => {
  const assignmentStatus = String(row.assignment_status || '').toLowerCase();
  const routeKey = ROUTE_KEY_BY_ASSIGNMENT_STATUS[assignmentStatus];

  const driverId = toNum(firstPresent(row.driver_id, row.id, row.driverId));
  const assignmentId = toNum(firstPresent(row.assignment_id, row.assignmentId));

  if (!routeKey) return null;

  return {
    ...row,
    result_key: `assignment-${
      assignmentId ?? driverId ?? `${routeKey}-${row.email || row.name || ''}`
    }`,
    routeKey,

    id: driverId,
    driver_id: driverId,
    name: firstPresent(row.name, row.driver_name, row.driver?.name, ''),
    email: firstPresent(row.email, row.driver?.email, ''),
    vehicle_registration_number: firstPresent(
      row.vehicle_registration_number,
      row.vehicleRegistrationNumber,
      row.vrn,
      row.driver?.vehicle_registration_number,
      ''
    ),

    agency_id: toNum(firstPresent(row.agency_id, row.agencyId, row.agency?.id)),
    agency_name: firstPresent(row.agency_name, row.agencyName, row.agency?.name, null),

    site_id: toNum(firstPresent(row.site_id, row.siteId, row.site?.id)),
    site_name: firstPresent(row.site_name, row.siteName, row.site?.name, null),

    assignment_id: assignmentId,
    assignment_status: assignmentStatus,

    ban_id: undefined,
    ban_reason: null,
  };
};

// Converts ban-backed rows into the shared drawer result shape.
const normalizeBannedResult = (row) => {
  const driverId = toNum(firstPresent(row.driver_id, row.id, row.driverId));
  const banId = toNum(firstPresent(row.ban_id, row.banId));

  return {
    ...row,
    result_key: `ban-${banId ?? driverId ?? row.email ?? row.name ?? ''}`,
    routeKey: 'banned',

    id: driverId,
    driver_id: driverId,
    name: firstPresent(row.name, row.driver_name, row.driver?.name, ''),
    email: firstPresent(row.email, row.driver?.email, ''),
    vehicle_registration_number: firstPresent(
      row.vehicle_registration_number,
      row.vehicleRegistrationNumber,
      row.vrn,
      row.driver?.vehicle_registration_number,
      ''
    ),

    agency_id: toNum(firstPresent(row.agency_id, row.agencyId, row.agency?.id)),
    agency_name: firstPresent(row.agency_name, row.agencyName, row.agency?.name, null),

    site_id: toNum(firstPresent(row.site_id, row.siteId, row.site?.id)),
    site_name: firstPresent(row.site_name, row.siteName, row.site?.name, null),

    assignment_id: toNum(firstPresent(row.assignment_id, row.assignmentId)),
    assignment_status: row.assignment_status || null,

    ban_id: banId,
    ban_reason: firstPresent(row.ban_reason, row.banReason, row.reason, null),
  };
};

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

export default function GlobalResultsDrawer({ onOpen, onViewAll, maxPerGroup = 5 }) {
  const { agencyId, q, scope, source } = useSelector(selectFilters);
  const selectedSiteId = useSelector(selectConcreteSelectedSiteId);
  const isAllSitesMode = useSelector(selectIsAllSitesMode);

  const query = (q || '').trim();
  const requestIdRef = useRef(0);

  const [state, setState] = useState({
    results: EMPTY_RESULTS,
    loading: false,
    error: null,
    query: '',
  });

  /* ------------------------------------------------------------------------ */
  /* Canonical global search fetch                                             */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    // Drawer is global-only. In tab mode, lane pages own searched fetching.
    if (scope !== 'global' || !query) {
      requestIdRef.current += 1;
      setState({
        results: EMPTY_RESULTS,
        loading: false,
        error: null,
        query: '',
      });
      return undefined;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    let cancelled = false;

    setState((prev) => ({
      ...prev,
      loading: true,
      error: null,
      query,
    }));

    // Debounce global search so every keystroke does not immediately fetch.
    const timer = window.setTimeout(async () => {
      const assignmentOptions = {
        statuses: ASSIGNMENT_STATUSES,
        agencyId,
        search: query,
        source,
      };

      const banOptions = {
        agencyId,
        search: query,
        source,
      };

      const hasAssignmentSite = isAllSitesMode || selectedSiteId;

      try {
        const [assignmentRows, bannedRows] = await Promise.all([
          hasAssignmentSite
            ? isAllSitesMode
              ? fetchCompanyAssignments(assignmentOptions)
              : fetchSiteAssignments(selectedSiteId, assignmentOptions)
            : Promise.resolve([]),

          fetchBannedDrivers(banOptions),
        ]);

        if (cancelled || requestId !== requestIdRef.current) return;

        const normalizedAssignments = (assignmentRows || [])
          .map(normalizeAssignmentResult)
          .filter(Boolean)
          .filter((row) => matchesSearch(row, query.toLowerCase()));

        const normalizedBanned = (bannedRows || [])
          .map(normalizeBannedResult)
          .filter((row) => matchesSearch(row, query.toLowerCase()));

        setState({
          results: {
            Active: normalizedAssignments.filter((r) => r.routeKey === 'active'),
            Reserved: normalizedAssignments.filter((r) => r.routeKey === 'reserved'),
            Requests: normalizedAssignments.filter((r) => r.routeKey === 'requests'),
            Banned: normalizedBanned,
          },
          loading: false,
          error: null,
          query,
        });
      } catch (err) {
        if (cancelled || requestId !== requestIdRef.current) return;

        setState({
          results: EMPTY_RESULTS,
          loading: false,
          error: err?.response?.data?.error || err?.message || 'Search failed',
          query,
        });
      }
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [agencyId, isAllSitesMode, query, scope, selectedSiteId, source]);

  /* ------------------------------------------------------------------------ */
  /* Derived UI state                                                          */
  /* ------------------------------------------------------------------------ */

  const hasResults = useMemo(
    () => Object.values(state.results).some((items) => items.length > 0),
    [state.results]
  );

  // Do not render anything unless global search is active.
  if (scope !== 'global' || !query) return null;

  /* ------------------------------------------------------------------------ */
  /* Result group renderer                                                     */
  /* ------------------------------------------------------------------------ */

  const Section = ({ title, items, routeKey }) => {
    if (!items?.length) return null;

    const rows = items.slice(0, maxPerGroup);

    return (
      <section className="rounded-2xl border border-app-border bg-app-surface p-3 sm:p-4">
        {/* Group heading */}
        <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <h4 className="text-sm font-bold text-app-text">{title}</h4>
            <p className="text-xs text-app-muted">
              {items.length} result{items.length === 1 ? '' : 's'}
            </p>
          </div>

          <button
            type="button"
            className="w-full rounded-xl border border-app-border bg-app-card px-3 py-2 text-xs font-semibold text-app-text transition hover:border-app-primary/40 hover:bg-app-surface sm:w-auto"
            onClick={() => onViewAll?.(routeKey)}
          >
            View all
          </button>
        </div>

        {/* Group rows */}
        <ul className="space-y-2">
          {rows.map((r) => (
            <li
              key={r.result_key}
              className="flex flex-col gap-3 overflow-hidden rounded-xl border border-app-border bg-app-card px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between"
            >
              {/* Driver identity + visible searchable fields */}
              <div className="min-w-0 w-full sm:flex-1">
                <p className="break-words font-medium text-app-text sm:truncate">
                  {highlightMatch(r.name || 'Unnamed driver', query)}
                </p>

                <p className="mt-1 break-words text-xs leading-5 text-app-muted sm:truncate">
                  {renderDriverMeta(r, query)}
                </p>
              </div>

              {/* Status badge + open action */}
              <div className="flex w-full flex-wrap items-center justify-between gap-2 sm:w-auto sm:shrink-0 sm:justify-end">
                <span className="rounded-full border border-app-border bg-app-surface px-3 py-1 text-xs font-semibold text-app-muted">
                  {TITLE_BY_ROUTE_KEY[routeKey]}
                </span>

                <button
                  type="button"
                  className="rounded-xl border border-app-primary bg-app-primary px-3 py-2 text-xs font-semibold text-white transition hover:opacity-90"
                  onClick={() => onOpen(r)}
                >
                  Open
                </button>
              </div>
            </li>
          ))}
        </ul>
      </section>
    );
  };

  /* ------------------------------------------------------------------------ */
  /* Render                                                                    */
  /* ------------------------------------------------------------------------ */

  return (
    <div className="mb-5 overflow-hidden rounded-2xl border border-app-border bg-app-card p-4 shadow-sm sm:p-5">
      {/* Drawer heading */}
      <div className="mb-4">
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-app-muted">
          Global Search
        </p>

        <h3 className="mt-1 break-words text-lg font-bold text-app-text">
          Results for "{query}"
        </h3>
      </div>

      <div className="space-y-3">
        {/* Loading state */}
        {state.loading && (
          <div className="rounded-2xl border border-app-border bg-app-surface px-4 py-3 text-sm font-medium text-app-muted">
            Searching...
          </div>
        )}

        {/* Error state */}
        {state.error && (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
            {state.error}
          </div>
        )}

        {/* Empty state */}
        {!state.loading && !state.error && !hasResults && (
          <div className="rounded-2xl border border-app-border bg-app-surface px-4 py-3 text-sm font-medium text-app-muted">
            No matching drivers found.
          </div>
        )}

        {/* Result groups */}
        <Section title="Active" items={state.results.Active} routeKey="active" />
        <Section title="Reserved" items={state.results.Reserved} routeKey="reserved" />
        <Section title="Requests" items={state.results.Requests} routeKey="requests" />
        <Section title="Banned" items={state.results.Banned} routeKey="banned" />
      </div>
    </div>
  );
}
